import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { matrixAllows } from '../../utils/matrixAllows';
import AppBootstrapSplash from './AppBootstrapSplash';
import AccessDenied from './AccessDenied';

/**
 * Guard de ruta por matriz efectiva (POS-PERM-LEGACY-001 / FIX-001).
 * Fail-closed mientras loading o sin permiso.
 * Prefiere matrixAllows(permisosEfectivos) — sin bypass ADMIN.
 *
 * POS-UX-CONTROL-RECOVERY-001: sin permiso → AccessDenied (NO Dashboard).
 *
 * Props:
 * - module + action: un permiso
 * - anyOf: [['modulo','accion'], ...] — basta uno true
 * - redirectTo: legado ignorado (se conserva prop por compatibilidad)
 */
const RequirePermission = ({ module, action, anyOf, children }) => {
  const { hasPermission, loading, permisosEfectivos } = useAuth();
  const location = useLocation();

  const check = (mod, act) => {
    if (permisosEfectivos && typeof permisosEfectivos === 'object') {
      return matrixAllows(permisosEfectivos, mod, act) === true;
    }
    return typeof hasPermission === 'function' && hasPermission(mod, act) === true;
  };

  let allowed = false;
  if (!loading) {
    if (Array.isArray(anyOf) && anyOf.length > 0) {
      allowed = anyOf.some(([mod, act]) => check(mod, act));
    } else if (module && action) {
      allowed = check(module, action);
    }
  }

  if (loading) {
    return (
      <AppBootstrapSplash
        productName="0E3 POS"
        message="Verificando permisos..."
        showVersion={false}
        fullViewport={false}
      />
    );
  }

  if (!allowed) {
    const needed = Array.isArray(anyOf) && anyOf.length
      ? anyOf.map(([m, a]) => `${m}.${a}`).join(' o ')
      : (module && action ? `${module}.${action}` : null);
    return (
      <AccessDenied
        title="Acceso denegado"
        message="No tenés permiso para abrir esta sección."
        detail={needed ? `Permiso requerido: ${needed}.` : null}
        backTo={location.state?.from?.pathname || '/'}
      />
    );
  }

  return children;
};

export default RequirePermission;
